import React, { useEffect, useRef } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat, toLonLat } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { Style, Icon } from "ol/style";

const OSMMap = ({ latitude, longitude, onLocationSelect, height = "50vh" }) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerSourceRef = useRef(null);
  
  // This lock prevents the map from stuttering when the user clicks
  const isUserClickingRef = useRef(false); 

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const lng = Number(longitude || 51.391173);
    const lat = Number(latitude || 35.700954);
    const coords = fromLonLat([lng, lat]);

    // 1. Create a native Vector Source for the marker (Zero DOM conflicts)
    const vectorSource = new VectorSource({
      features: [new Feature(new Point(coords))],
    });
    markerSourceRef.current = vectorSource;

    // 2. Create the red pin using a clean SVG data URI
    const pinSVG = '<svg width="32" height="48" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.373 0 12c0 7.75 12 24 12 24s12-16.25 12-24C24 5.373 18.627 0 12 0zm0 17.5c-3.038 0-5.5-2.462-5.5-5.5S8.962 6.5 12 6.5s5.5 2.462 5.5 5.5-2.462 5.5-5.5 5.5z" fill="#dc3545"/></svg>';
    const pinIcon = new Style({
      image: new Icon({
        src: 'data:image/svg+xml;utf8,' + encodeURIComponent(pinSVG),
        anchor: [0.5, 1], // Anchor at the bottom tip of the pin
        scale: 0.5,
      }),
    });

    const markerLayer = new VectorLayer({
      source: vectorSource,
      style: pinIcon,
      zIndex: 100, // Ensure it is always on top
    });

    // 3. Initialize Map
    const map = new Map({
      target: mapContainerRef.current,
      layers: [new TileLayer({ source: new OSM() }), markerLayer],
      view: new View({ center: coords, zoom: 14 }),
    });

    // 4. Click Handler
    if (onLocationSelect) {
      map.on("click", (e) => {
        // Lock the animation watcher so it doesn't fight the user
        isUserClickingRef.current = true; 
        
        const [newLng, newLat] = toLonLat(e.coordinate);

        // Move the pin instantly on the canvas
        vectorSource.getFeatures()[0].setGeometry(new Point(e.coordinate));

        // Update React state
        onLocationSelect(newLat, newLng);

        // Unlock after React has time to process the state change
        setTimeout(() => { isUserClickingRef.current = false; }, 300);
      });
    }

    mapInstanceRef.current = map;

    return () => map.setTarget(null);
  }, []);

  // 5. Watch for external coordinate changes (e.g. initial load or clicking a "My Location" button)
  useEffect(() => {
    if (mapInstanceRef.current && markerSourceRef.current && latitude && longitude) {
      
      // If the user just clicked the map, DO NOT animate. Just let the click handler do its job.
      if (isUserClickingRef.current) return; 

      const coords = fromLonLat([Number(longitude), Number(latitude)]);
      markerSourceRef.current.getFeatures()[0].setGeometry(new Point(coords));
      mapInstanceRef.current.getView().animate({ center: coords, duration: 500 });
    }
  }, [latitude, longitude]);

  return (
    <div 
      ref={mapContainerRef} 
      style={{ 
        width: "100%", 
        height: height, 
        cursor: onLocationSelect ? "pointer" : "default" 
      }} 
    />
  );
};

export default OSMMap;